import { Button, Divider } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import DropZone from "../../../../../components/app/DropZone";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { usePatientEmailSend } from "../../../../../queries/patient/use-patient-email-send";
import { useRosTemplateById } from "../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../queries/template/new-flow/use-new-template-list";
import { GetAllClaimTemplateTypesEnum } from "../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import * as Yup from "yup";
import RichTextEditor from "../../../../../components/richTextEditor/RichTextEditor";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";

interface PatientSendBulkEmailModalProps {
  disClosureHost: DisclosureHost;
  onSave?: () => void;
}

const PatientSendBulkEmailModal = ({
  disClosureHost,
  onSave,
}: PatientSendBulkEmailModalProps) => {
  const [patient] = useCurrentUser();
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [searchTemplate, setSearchTemplate] = useDebounce<string>("");
  const [templatePagination, setTemplatePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: templateList,
    isLoading,
    isRefetching,
  } = useNewTemplateList({
    pagination: templatePagination,
    searchKey: searchTemplate,
    templateType: GetAllClaimTemplateTypesEnum.Email,
    xTENANTID: getXtenantId(),
  });

  const { mutateAsync: sendMail } = usePatientEmailSend();

  const { data: selectedTemplateData, isLoading: selectedTemplateLoading } =
    useRosTemplateById({
      rosTemplateUuid: selectedTemplate?.value,
      xTENANTID: getXtenantId(),
    });

  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (selectedTemplateData?.data && selectedTemplate?.value) {
      formik.setFieldValue(
        "description",
        selectedTemplateData?.data?.description
      );
      formik.setFieldValue("subject", selectedTemplateData?.data?.subject);
    }
  }, [selectedTemplateData?.data, selectedTemplate?.value]);

  const closeAndReset = () => {
    setSelectedTemplate(undefined);
    disClosureHost.close();
    formik.resetForm();
  };

  const handleSendMail = (v: {
    toEmail: string;
    fromEmail: string;
    subject: string;
    description: string;
    attachment?: File;
  }) => {
    const payload = {
      toEmail: v.toEmail,
      fromEmail: v.fromEmail,
      subject: v.subject,
      description: v.description,
      xTENANTID: getXtenantId(),
      attachment: v.attachment,
    };
    sendMail(payload)
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        onSave?.();
        closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const formik = useFormik({
    initialValues: {
      toEmail: "",
      fromEmail: patient?.email || "",
      subject: "",
      description: "",
      attachment: undefined as File | undefined,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      toEmail: Yup.string().email("Invalid email").required().label("To"),
      fromEmail: Yup.string().email("Invalid email").required().label("From"),
      subject: Yup.string().required().label("Subject"),
      description: Yup.string().required().label("Description"),
    }),
    onSubmit: handleSendMail,
  });

  return (
    <SideSheetDrawer
      open={disClosureHost.opened}
      w="872px"
      title="New Mail"
      onClose={closeAndReset}
      footerActions={
        <Button variant="contained" onClick={formik.submitForm}>
          Send
        </Button>
      }
    >
      <LoadingOverlay isLoading={selectedTemplateLoading} />

      <Flex flexDirection={"column"}>
        <Divider sx={{ marginBottom: 2, borderColor: "gray" }} />

        <Flex width={"100%"} gap={2} alignItems={"center"}>
          <Text color={"#1A1A1A66"} sx={{ minWidth: "40px" }}>
            To
          </Text>
          <Input
            required
            label=""
            textFieldProps={{
              fullWidth: true,
              name: "toEmail",
              onBlur: formik.handleBlur,
              value: formik.values?.toEmail,
              onChange: formik.handleChange,
              placeholder: "Enter To Email",
            }}
            error={formik.touched?.toEmail && (formik.errors?.toEmail as any)}
          />
        </Flex>

        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <AsyncSelectScroll
          loading={isLoading || isRefetching}
          placeholder="Select Template"
          onSearch={(val) => {
            setSearchTemplate(val);
            setTemplatePagination((p) => ({ ...p, page: 0 }));
          }}
          options={templateList?.data?.content?.map((it) => ({
            value: it?.uuid,
            label: it?.templateName,
          }))}
          value={selectedTemplate}
          onChange={(val) => setSelectedTemplate(val)}
          pagination={templatePagination}
          setPagination={setTemplatePagination}
          totalElements={templateList?.data?.page?.totalElements}
        />
        <Divider sx={{ marginY: 2, borderColor: "gray" }} />

        <Flex width={"100%"} gap={2} alignItems={"center"}>
          <Text color={"#1A1A1A66"}>Subject</Text>
          <Input
            required
            label=""
            textFieldProps={{
              fullWidth: true,
              name: "subject",
              onBlur: formik.handleBlur,
              value: formik.values?.subject,
              onChange: formik.handleChange,
              placeholder: "Enter Subject",
            }}
            error={formik.touched?.subject && (formik.errors?.subject as any)}
          />
        </Flex>
        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <RichTextEditor
          name="description"
          value={formik.values?.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched?.description && (formik.errors?.description as any)
          }
          placeholder="Enter Description"
          minRows={10}
        />
        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <DropZone
          supportedFile="JPEG, PNG, PDF, Word"
          value={formik.values?.attachment?.name as any}
          height={"120px"}
          accept="all"
          content="Drag & Drop attachment Or Browse Files"
          onClick={(file) => {
            const curFile = file?.item(0);
            formik.setFieldValue("attachment", curFile || undefined);
          }}
        />
      </Flex>
    </SideSheetDrawer>
  );
};

export default PatientSendBulkEmailModal;
